import { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Clock, Landmark, Lock, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStakingStatus } from "@/hooks/useStakingStatus";
import { cn } from "@/lib/utils";

function StakingComingSoon({ className }: { className?: string }) {
  const navigate = useNavigate();

  return (
    <div className={cn("mx-auto max-w-5xl space-y-6", className)}>
      <section className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-card to-background p-6 text-center sm:p-10">
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-5">
          <Badge variant="secondary" className="gap-1.5">
            <Landmark className="h-3.5 w-3.5" /> Staking
          </Badge>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Staking subscriptions are coming soon</h1>
          <p className="text-sm text-muted-foreground sm:text-base">
            Plan browsing stays open, but new staking subscriptions are locked until the product goes live.
          </p>
          <Button onClick={() => navigate("/app/staking")} className="gap-2">
            <Landmark className="h-4 w-4" /> View staking plans
          </Button>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            <span>No funds are deducted and no positions are created until launch.</span>
          </div>
        </div>
      </section>

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          { icon: ShieldCheck, title: "Launch controlled", desc: "Admins can turn staking live when the product is ready." },
          { icon: Clock, title: "Plans visible", desc: "Upcoming plans remain visible for review before launch." },
          { icon: Lock, title: "Actions locked", desc: "Subscribe and stake actions stay disabled until live mode is enabled." },
        ].map((item) => (
          <Card key={item.title}>
            <CardContent className="space-y-3 p-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <item.icon className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold">{item.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">{item.desc}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function StakingGate({ children }: { children: ReactNode }) {
  const { locked, loading } = useStakingStatus();
  if (loading) return null;
  if (locked) {
    return (
      <div className="p-4 sm:p-6">
        <StakingComingSoon />
      </div>
    );
  }
  return <>{children}</>;
}

export function StakingLockedNotice({ className }: { className?: string }) {
  const { locked } = useStakingStatus();
  if (!locked) return null;
  return (
    <Alert className={className}>
      <Lock className="h-4 w-4" />
      <AlertTitle>Staking is launching soon</AlertTitle>
      <AlertDescription>
        You can browse available plans, but subscriptions stay locked until staking goes live.
      </AlertDescription>
    </Alert>
  );
}
